import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, isBackendConfigured } from '../utils/backendApi';
import useTelemetry from '../components/command/useTelemetry';
import { deriveFleet } from '../components/three/fleetState';
import LiveFrame from './live/LiveFrame';
import SecurityPosture from './live/SecurityPosture';
import BehaviorBaseline from './live/BehaviorBaseline';
import TrafficBoard from './live/TrafficBoard';
import { IncidentsBoard, HostsBoard, StandbyBoard, PlaneBoard, ReleaseBoard } from './live/FleetBoards';
import { FLEET_LIVE } from './live/routes';
import './AdminApps.css';

const COPY = {
  security: ['Security posture', 'Defense in depth · runtime, base image, images, web, CI, dependencies'],
  incidents: ['Incidents', 'Open and recent'],
  hosts: ['Hosts', 'Slots, memory, and disk'],
  traffic: ['Traffic', 'Edge rollups and inter-app flows'],
  anomalies: ['Anomalies', 'How the fleet is thresholded, and what is flagged'],
  standby: ['Standby runners', 'Warm browser and job tasks'],
  release: ['Release', 'Pinned kit and who is off it'],
  plane: ['Control plane', 'Month-to-date spend by service'],
};

export default function AdminFleetLive() {
  const { aspect } = useParams();
  const [instances, setInstances] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [costData, setCostData] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [standby, setStandby] = useState(null);
  const [pinnedKitRef, setPinnedKitRef] = useState('');
  const [error, setError] = useState(null);
  const known = FLEET_LIVE.includes(aspect);
  const wantsTelemetry = known && ['traffic', 'anomalies'].includes(aspect);

  const load = useCallback(async () => {
    const [i, inc, co, h, ls, sb] = await Promise.all([
      apiGet('/apps/admin/instances'),
      apiGet('/apps/admin/incidents').catch(() => ({ incidents: [] })),
      apiGet('/apps/admin/costs').catch(() => null),
      apiGet('/apps/admin/hosts').catch(() => ({ hosts: [] })),
      apiGet('/apps/admin/launch-status').catch(() => ({})),
      apiGet('/apps/admin/standby').catch(() => ({ standby: null })),
    ]);
    setInstances(i.instances || []);
    setIncidents(inc.incidents || []);
    setCostData(co);
    setHosts(h.hosts || []);
    setStandby(sb.standby || null);
    setPinnedKitRef(ls.kitRef || 'main');
  }, []);

  useEffect(() => {
    if (!isBackendConfigured()) { setError('Backend API is not configured.'); return undefined; }
    let alive = true;
    load().catch((err) => { if (alive) setError(err.status === 403 ? 'Operator access required.' : err.message); });
    const t = setInterval(() => { load().catch(() => {}); }, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [load]);

  const fleet = useMemo(() => (instances ? deriveFleet({ mode: 'admin', instances, incidents, costs: costData, hosts, pinnedKitRef }) : null), [instances, incidents, costData, hosts, pinnedKitRef]);
  const roster = useMemo(() => (instances || []).filter((i) => i.slug && i.status !== 'terminated').map((i) => ({ slug: i.slug, envRole: i.envRole })), [instances]);
  const fetchReal = useCallback((w) => apiGet(`/apps/admin/telemetry?window=${w}`), []);
  const { telemetry, controls } = useTelemetry({ surface: 'fleet-live', roster, fetchReal, enabled: wantsTelemetry && !!instances });
  const [title, meta] = COPY[aspect] || ['Fleet', ''];
  const back = '/admin/apps';

  if (error) {
    return <LiveFrame back={back} backLabel="← Fleet" title="Fleet" meta=""><p className="cs-help">{error}</p></LiveFrame>;
  }
  if (!known) {
    return (
      <LiveFrame back={back} backLabel="← Fleet" title="Fleet" meta="That view is not on the operator fleet.">
        <p className="cs-help">{FLEET_LIVE.join(' · ')}</p>
      </LiveFrame>
    );
  }

  return (
    <LiveFrame back={back} backLabel="← Fleet" title={title} meta={aspect === 'plane' && costData?.period ? `${meta} · ${costData.period}` : meta}>
      {instances == null && <p className="cs-help">Loading…</p>}
      {aspect === 'security' && <SecurityPosture />}
      {aspect === 'incidents' && <IncidentsBoard incidents={incidents} />}
      {aspect === 'hosts' && <HostsBoard hosts={fleet?.hosts || []} />}
      {aspect === 'traffic' && <TrafficBoard telemetry={telemetry} controls={controls} />}
      {aspect === 'anomalies' && <BehaviorBaseline telemetry={telemetry} controls={controls} />}
      {aspect === 'standby' && <StandbyBoard standby={standby} />}
      {aspect === 'release' && <ReleaseBoard instances={instances || []} pinnedKitRef={pinnedKitRef} />}
      {aspect === 'plane' && <PlaneBoard plane={costData?.plane} period={costData?.period} />}
    </LiveFrame>
  );
}
